/**
 * Application Constants
 * Centralized configuration for timeouts, limits, and thresholds
 */

// ============================================================================
// Time Constants (in milliseconds)
// ============================================================================

/**
 * 30 seconds in milliseconds
 * Used for short-lived cache/stale times
 */
export const THIRTY_SECONDS_MS = 30 * 1000;

/**
 * 1 minute in milliseconds
 */
export const ONE_MINUTE_MS = 60 * 1000;

/**
 * 5 minutes in milliseconds
 * Used for moderate cache/stale times
 */
export const FIVE_MINUTES_MS = 5 * ONE_MINUTE_MS;

/**
 * 1 hour in milliseconds
 */
export const ONE_HOUR_MS = 60 * ONE_MINUTE_MS;

/**
 * 1 day in milliseconds
 */
export const ONE_DAY_MS = 24 * ONE_HOUR_MS;

// ============================================================================
// Authentication Constants
// ============================================================================

/**
 * Access token lifetime
 * Default: 8 hours (one working day)
 */
export const ACCESS_TOKEN_LIFETIME_MS = 8 * ONE_HOUR_MS;

/**
 * Duration a refresh token remains valid (sliding expiration window)
 * Security: Sliding window - extends on each use, inactive users logged out after this period
 * Default: 7 days from last use
 */
export const REFRESH_TOKEN_SLIDING_WINDOW_MS = 7 * ONE_DAY_MS;

/**
 * Maximum absolute session lifetime
 * Security: Hard limit prevents indefinite session extension via continuous refresh
 * Users must re-authenticate after this period regardless of activity
 * Default: 90 days from initial login
 */
export const MAX_SESSION_LIFETIME_MS = 90 * ONE_DAY_MS;

/**
 * Time buffer before access token expiration to trigger automatic refresh
 * Default: 3 minutes before expiration
 */
export const REFRESH_BUFFER_MS = 3 * ONE_MINUTE_MS;

/**
 * Duration an email verification token remains valid
 * Default: 24 hours
 */
export const EMAIL_VERIFICATION_EXPIRY_MS = 24 * ONE_HOUR_MS;

/**
 * Duration a password reset token remains valid
 * Default: 60 minutes
 */
export const PASSWORD_RESET_EXPIRY_MS = ONE_HOUR_MS;

/**
 * Maximum number of failed login attempts before account lockout
 * Default: 5 attempts
 */
export const MAX_FAILED_LOGIN_ATTEMPTS = 5;

/**
 * Duration an account remains locked after exceeding failed login attempts
 * Security: Increased from 15 to 30 minutes (stronger protection)
 * Default: 30 minutes
 */
export const ACCOUNT_LOCKOUT_DURATION_MS = 30 * ONE_MINUTE_MS;

/**
 * Cooldown period before an employee can be reactivated after deactivation
 * Purpose: Prevents gaming the license system by rapid employee rotation
 * Default: 24 hours
 */
export const EMPLOYEE_REACTIVATION_COOLDOWN_MS = 24 * ONE_HOUR_MS;
export const EMPLOYEE_REACTIVATION_COOLDOWN_ACTIVE_CODE =
  'EMPLOYEE_REACTIVATION_COOLDOWN_ACTIVE';

/**
 * Retention period for license usage snapshots
 * Purpose: Keep sufficient history for billing disputes and analysis
 * Default: 13 months (1 year + 1 month buffer)
 */
export const LICENSE_SNAPSHOT_RETENTION_MONTHS = 13;

/**
 * Abuse pattern detection thresholds
 */
export const ABUSE_HIGH_FREQUENCY_THRESHOLD = 5; // Deactivation cycles per employee per month
export const ABUSE_WEEKEND_DEACTIVATION_THRESHOLD = 3; // Weekend deactivations per month
export const ABUSE_PEAK_VS_AVERAGE_THRESHOLD = 50; // Percentage difference
export const ABUSE_CRITICAL_CYCLING_THRESHOLD = 10; // Cycles for critical severity
export const ABUSE_CRITICAL_WEEKEND_THRESHOLD = 6; // Weekend deactivations for high severity
export const ABUSE_CRITICAL_PEAK_THRESHOLD = 100; // Peak vs average for critical severity

// ============================================================================
// System/Default Values
// ============================================================================

/**
 * System Administration company (superadmin + platform automation actors).
 */
export const SYSTEM_COMPANY_ID = '00000000-0000-0000-0000-000000000000';

/**
 * Non-login automation person/employee for cron jobs, queue workers, and integrations.
 * Referenced by employee FK columns (`created_by`, `updated_by`, `actor_id`, etc.).
 *
 * Fixed UUIDv7 pair generated from 2024-01-01T00:00:00.000Z (see uuidv7.ts).
 */
export const SYSTEM_EMPLOYEE_PERSON_ID = '018cc251-f400-7c09-8411-1ffd04321231';
export const SYSTEM_EMPLOYEE_ID = '018cc251-f400-7c09-8411-1ffe1cd84d81';

export function isSystemEmployeeId(
  employeeId: string | null | undefined
): boolean {
  return employeeId === SYSTEM_EMPLOYEE_ID;
}

// ============================================================================
// Rate Limiting Constants
// ============================================================================

/**
 * Time window for rate limiting (sliding window)
 * Default: 15 minutes
 */
export const RATE_LIMIT_WINDOW_MS = 15 * ONE_MINUTE_MS;

/**
 * Maximum number of requests allowed per IP in the0 rate limit window
 * Applied to authentication endpoints
 * Default: 5 requests per 15 minutes
 */
export const RATE_LIMIT_MAX_REQUESTS = 50;

/**
 * Strict rate limit for write-heavy operations (CRUD operations)
 * Default: 500 requests per 15 minutes
 */
export const RATE_LIMIT_STRICT_MAX_REQUESTS = 500;

/**
 * Moderate rate limit for read-heavy operations (list queries)
 * Default: 1000 requests per 15 minutes
 */
export const RATE_LIMIT_MODERATE_MAX_REQUESTS = 1000;

/**
 * Default per-user rate limit (company-configurable)
 * Applied to individual authenticated users
 * Default: 1000 requests per 15 minutes
 */
export const DEFAULT_RATE_LIMIT_USER_MAX = 1000;

/**
 * Default per-company rate limit (company-configurable)
 * Applied to entire company (sum of all employees)
 * Default: 1000000 requests per 15 minutes
 */
export const DEFAULT_RATE_LIMIT_COMPANY_MAX = 1000000;

/**
 * Default per-IP rate limit (company-configurable)
 * Applied for DDoS protection only (should be high)
 * Default: 500000 requests per 15 minutes
 */
export const DEFAULT_RATE_LIMIT_IP_MAX = 500000;

/**
 * Minimum allowed rate limit value
 * Prevents setting limits too low
 */
export const MIN_RATE_LIMIT = 1;

/**
 * Maximum allowed per-user rate limit
 * Prevents abuse of rate limit configuration
 */
export const MAX_RATE_LIMIT_USER = 100000;

/**
 * Maximum allowed per-company rate limit
 * Prevents abuse of rate limit configuration
 */
export const MAX_RATE_LIMIT_COMPANY = 1000000;

/**
 * Maximum allowed per-IP rate limit
 * Prevents abuse of rate limit configuration
 */
export const MAX_RATE_LIMIT_IP = 1000000;

// ============================================================================
// Monetary Precision Constants
// ============================================================================

/**
 * Number of decimal places for monetary values
 * ALL financial values in the system MUST be rounded to this precision.
 * This includes: prices, amounts, balances, payments, interest, penalties, etc.
 *
 * STRUCTURAL REQUIREMENT: This is enforced at multiple layers:
 * - Database: ROUND(x, 2) in VIEWs and queries
 * - Import scripts: roundMoney() function before storage
 * - Backend services: roundMoney() on all monetary calculations
 * - Frontend: Display only (backend provides rounded values)
 *
 * Default: 2 decimal places (standard for PEN and USD currencies)
 */
export const MONEY_DECIMAL_PLACES = 2;

// ============================================================================
// Validation Constants
// ============================================================================

/**
 * Minimum password length
 * Default: 8 characters
 */
export const MIN_PASSWORD_LENGTH = 8;

/**
 * Maximum email length
 * Default: 255 characters (database constraint)
 */
export const MAX_EMAIL_LENGTH = 255;

/**
 * Maximum phone number length
 * Default: 20 characters (international format support)
 */
export const MAX_PHONE_LENGTH = 20;

/**
 * Minimum digits required for phone validation
 * Phone numbers must have at least this many digits to be valid
 * Default: 7 digits (shortest valid phone numbers)
 */
export const MIN_PHONE_DIGITS = 7;

/**
 * Minimum digit ratio for phone validation
 * At least this fraction of non-space characters must be digits
 * Default: 0.5 (50% digits)
 */
export const PHONE_MIN_DIGIT_RATIO = 0.5;

/**
 * Maximum domain length (RFC 1035)
 * Default: 253 characters (DNS standard maximum)
 */
export const MAX_DOMAIN_LENGTH = 253;

/**
 * Maximum subdomain length (RFC 1035 label limit)
 * Default: 63 characters
 */
export const MAX_SUBDOMAIN_LENGTH = 63;

/**
 * Minimum subdomain length
 * Default: 3 characters
 */
export const MIN_SUBDOMAIN_LENGTH = 3;

/**
 * Maximum name length
 * Default: 100 characters (reasonable limit for personal names)
 */
export const MAX_NAME_LENGTH = 100;

/**
 * Minimum username length
 * Default: 3 characters
 */
export const MIN_USERNAME_LENGTH = 3;

/**
 * Maximum username length
 * Default: 50 characters
 */
export const MAX_USERNAME_LENGTH = 50;

/**
 * Maximum job title length
 * Default: 100 characters
 */
export const MAX_JOB_TITLE_LENGTH = 100;

/**
 * Maximum ID number length
 * Default: 20 characters (covers DNI, CE, Passport)
 */
export const MAX_ID_NUMBER_LENGTH = 20;

/**
 * Maximum external S10 code length
 * Default: 50 characters
 */
export const MAX_EXTERNAL_CODE_LENGTH = 50;

/**
 * Maximum source field length
 * Default: 255 characters (database constraint)
 */
export const MAX_SOURCE_LENGTH = 255;

/**
 * Maximum deactivation reason length
 * Default: 500 characters
 */
export const MAX_REASON_LENGTH = 500;

/**
 * Maximum publisher identity verification note length
 * Default: 500 characters
 */
export const MAX_PUBLISHER_IDENTITY_VERIFICATION_NOTE_LENGTH = 500;

/**
 * DNI (Documento Nacional de Identidad) length
 * Peru's national ID - exactly 8 digits
 */
export const DNI_LENGTH = 8;

/**
 * RUC (Registro Único de Contribuyente) length
 * Peru's tax ID - exactly 11 digits
 */
export const RUC_LENGTH = 11;

/**
 * CE (Carnet de Extranjería) special format length
 * Foreign resident ID - 9 digits starting with "00"
 */
export const CE_SPECIAL_LENGTH = 9;

/**
 * Minimum CE (Carnet de Extranjería) length
 * Foreign resident ID - 1-20 alphanumeric characters
 */
export const MIN_CE_LENGTH = 1;

/**
 * Maximum CE (Carnet de Extranjería) length
 * Foreign resident ID - 1-20 alphanumeric characters
 */
export const MAX_CE_LENGTH = 20;

/**
 * Minimum passport length
 * ICAO 9303 standard - 6-9 alphanumeric characters
 */
export const MIN_PASSPORT_LENGTH = 6;

/**
 * Maximum passport length
 * ICAO 9303 standard - 6-9 alphanumeric characters
 */
export const MAX_PASSPORT_LENGTH = 9;

// ============================================================================
// Data Retention Constants
// ============================================================================

/**
 * Number of days soft-deleted records are retained before permanent deletion
 * Default: 30 days
 */
export const SOFT_DELETE_RETENTION_DAYS = 30;

/**
 * Idempotency key TTL (in seconds)
 * Used to cache responses for duplicate POST/PUT/PATCH/DELETE requests.
 * Requests with the same X-Idempotency-Key within this window return cached response.
 * Default: 24 hours (86400 seconds)
 */
export const IDEMPOTENCY_KEY_TTL_SECONDS = 86400;

/**
 * HTTP Status: OK
 */
export const HTTP_STATUS_OK = 200;

/**
 * HTTP Status: Created
 */
export const HTTP_STATUS_CREATED = 201;

/**
 * HTTP Status: No Content (success with no response body)
 */
export const HTTP_STATUS_NO_CONTENT = 204;

/**
 * HTTP Status: Multiple Choices (start of 3xx redirect range)
 * Used for status range checks: status >= 200 && status < 300
 */
export const HTTP_STATUS_MULTIPLE_CHOICES = 300;

/**
 * HTTP Status: Bad Request
 */
export const HTTP_STATUS_BAD_REQUEST = 400;

/**
 * HTTP Status: Unauthorized
 */
export const HTTP_STATUS_UNAUTHORIZED = 401;

/**
 * HTTP Status: Forbidden
 */
export const HTTP_STATUS_FORBIDDEN = 403;

/**
 * HTTP Status: Not Found
 */
export const HTTP_STATUS_NOT_FOUND = 404;

/**
 * HTTP Status: Conflict (e.g., duplicate resource)
 */
export const HTTP_STATUS_CONFLICT = 409;

/**
 * HTTP Status: Upgrade Required
 */
export const HTTP_STATUS_UPGRADE_REQUIRED = 426;

/**
 * HTTP Status: Too Many Requests
 */
export const HTTP_STATUS_TOO_MANY_REQUESTS = 429;

/**
 * HTTP Status: Internal Server Error
 */
export const HTTP_STATUS_INTERNAL_SERVER_ERROR = 500;

/**
 * HTTP Status: Bad Gateway
 */
export const HTTP_STATUS_BAD_GATEWAY = 502;

/**
 * HTTP Status: Service Unavailable
 */
export const HTTP_STATUS_SERVICE_UNAVAILABLE = 503;

/**
 * HTTP Status: Gateway Timeout
 */
export const HTTP_STATUS_GATEWAY_TIMEOUT = 504;

// ============================================================================
// Webhook Constants
// ============================================================================

/**
 * Maximum allowed retry attempts for webhook delivery
 * Used for validation in webhook creation/update
 */
export const MAX_WEBHOOK_RETRY_ATTEMPTS = 10;

/**
 * Default retry attempts for webhook delivery
 * Used when no retry max is specified
 */
export const DEFAULT_WEBHOOK_RETRY_ATTEMPTS = 3;

/**
 * Webhook execution timeout in milliseconds
 * Default: 30 seconds (matches typical API gateway timeouts)
 */
export const WEBHOOK_TIMEOUT_MS = 30000;

/**
 * Base delay for exponential backoff in webhook retries (ms)
 * Actual delay: baseDelay * 2^attempt (1s, 2s, 4s, etc.)
 */
export const WEBHOOK_RETRY_BASE_DELAY_MS = 1000;

/**
 * Maximum delay between webhook retries (ms)
 * Caps exponential backoff at 30 seconds
 */
export const WEBHOOK_RETRY_MAX_DELAY_MS = 30000;

// ============================================================================
// Cryptography Constants
// ============================================================================

/**
 * BCrypt salt rounds for password hashing
 * Security: Increased from 8 to 10 rounds (improved security)
 * Default: 10 rounds (industry standard)
 */
export const BCRYPT_SALT_ROUNDS = 10;

/**
 * Random token length in bytes
 * Default: 32 bytes (256 bits)
 */
export const TOKEN_LENGTH_BYTES = 32;

/**
 * "Bearer " prefix length for Authorization header parsing
 * Default: 7 characters
 */
export const BEARER_PREFIX_LENGTH = 7;

/**
 * Milliseconds in one second (for TTL conversions)
 */
export const MILLISECONDS_PER_SECOND = 1000;

/**
 * Time unit constants for expiration calculations
 */
export const HOURS_PER_DAY = 24;
export const MINUTES_PER_HOUR = 60;
export const SECONDS_PER_MINUTE = 60;
export const DAYS_PER_WEEK = 7;
export const MS_PER_DAY =
  HOURS_PER_DAY *
  MINUTES_PER_HOUR *
  SECONDS_PER_MINUTE *
  MILLISECONDS_PER_SECOND;

/**
 * Default reservation duration in days
 */
export const DEFAULT_RESERVATION_DAYS = DAYS_PER_WEEK;

/**
 * Default workflow priority for custom workflows (higher = runs first)
 */
export const DEFAULT_WORKFLOW_PRIORITY = 100;

// ============================================================================
// Pagination Constants
// ============================================================================

/**
 * Default page size for list endpoints
 * Reduced from 100 to 25 for better performance (fewer items to serialize/transfer)
 */
export const DEFAULT_PAGE_LIMIT = 25;

/**
 * Maximum page size allowed
 */
export const MAX_PAGE_LIMIT = 1000;

/**
 * Maximum records for XLS export operations.
 * Exports should not be capped by page limits — this allows
 * exporting large datasets (up to 100k rows per export).
 */
export const MAX_EXPORT_RECORDS = 100_000;

/**
 * Page limit for Kanban board views
 * Matches Pipedrive's max API limit (500) for performance
 * Salesforce limits Kanban to 200 cards
 */
export const KANBAN_PAGE_LIMIT = 500;

/**
 * Default limit for task/activity queries
 */
export const DEFAULT_TASKS_LIMIT = 50;

/**
 * Default limit for audience preview results
 */
export const AUDIENCE_PREVIEW_DEFAULT_LIMIT = 20;

/**
 * Maximum limit for audience preview results
 */
export const AUDIENCE_PREVIEW_MAX_LIMIT = 100;

/**
 * Default threshold percentage for detecting "high discount" proformas
 * in data consistency checks. Discounts above this percentage are flagged
 * as potentially unrealistic (unless they are 100% which is a separate category).
 */
export const DEFAULT_DISCOUNT_THRESHOLD_PERCENT = 50;

// ============================================================================
// AI Training Constants
// ============================================================================

/**
 * Maximum training samples to fetch from database per training run
 * Balances memory usage (Workers 128MB limit) with dataset size
 * Used for simulated model retraining (Workers AI doesn't support actual training)
 * Default: 10,000 samples
 */
export const MAX_AI_TRAINING_SAMPLES = 10000;

// ============================================================================
// Real Estate Constants
// ============================================================================

/**
 * Maximum real estate title length
 */
export const MAX_REAL_ESTATE_TITLE_LENGTH = 255;

/**
 * Maximum real estate description length
 */
export const MAX_REAL_ESTATE_DESCRIPTION_LENGTH = 5000;

/**
 * Maximum address length
 */
export const MAX_ADDRESS_LENGTH = 500;

/**
 * Maximum city name length
 */
export const MAX_CITY_LENGTH = 100;

/**
 * Maximum state name length
 */
export const MAX_STATE_LENGTH = 100;

/**
 * Maximum country name length
 */
export const MAX_COUNTRY_LENGTH = 100;

/**
 * Maximum postal code length
 */
export const MAX_POSTAL_CODE_LENGTH = 20;

// ============================================================================
// Person Demographics Constants
// ============================================================================

/**
 * Maximum person title length (Sr, Sra, Ing, Dr, etc.)
 * Default: 20 characters
 */
export const MAX_PERSON_TITLE_LENGTH = 20;

/**
 * Maximum nationality length
 * Default: 100 characters
 */
export const MAX_NATIONALITY_LENGTH = 100;

/**
 * Maximum occupation length
 * Default: 100 characters
 */
export const MAX_OCCUPATION_LENGTH = 100;

/**
 * Maximum province name length
 * Default: 100 characters
 */
export const MAX_PROVINCE_LENGTH = 100;

/**
 * Maximum district name length
 * Default: 100 characters
 */
export const MAX_DISTRICT_LENGTH = 100;

/**
 * Maximum address reference length
 * Default: 500 characters
 */
export const MAX_ADDRESS_REFERENCE_LENGTH = 500;

/**
 * Minimum latitude value (degrees)
 */
export const MIN_LATITUDE = -90;

/**
 * Maximum latitude value (degrees)
 */
export const MAX_LATITUDE = 90;

/**
 * Minimum longitude value (degrees)
 */
export const MIN_LONGITUDE = -180;

/**
 * Maximum longitude value (degrees)
 */
export const MAX_LONGITUDE = 180;

// ============================================================================
// Financing Partner Constants
// ============================================================================

/**
 * Default list of Peruvian financial institutions for financing partners
 * These are seeded for all companies by default (as inactive) and can be managed by admins
 * Each partner includes a name and a short code for easy reference
 */
export const DEFAULT_FINANCING_PARTNERS = [
  { name: 'Agrobanco', code: 'AGRO' },
  { name: 'Alfin Banco', code: 'ALFIN' },
  { name: 'Banco Azteca', code: 'AZTECA' },
  { name: 'Banco Cencosud', code: 'CENCOSUD' },
  { name: 'Banco de Comercio', code: 'COMERCIO' },
  { name: 'Banco de Crédito del Perú (BCP)', code: 'BCP' },
  { name: 'Banco de la Nación', code: 'NACION' },
  { name: 'Banco Empresa', code: 'EMPRESA' },
  { name: 'Banco Falabella', code: 'FALABELLA' },
  { name: 'Banco GNB Perú', code: 'GNB' },
  { name: 'Banco hsbc Bank Peru s.a.', code: 'HSBC' },
  { name: 'Banco Interamericano de Finanzas (BanBif)', code: 'BANBIF' },
  { name: 'Banco Pichincha', code: 'PICHINCHA' },
  { name: 'Banco Ripley', code: 'RIPLEY' },
  { name: 'Banco Santander Perú', code: 'SANTANDER' },
  { name: 'BBVA Perú', code: 'BBVA' },
  { name: 'Caja Metropolitana de Lima', code: 'METROLIMA' },
  { name: 'Caja Municipal De Ahorro', code: 'CMAHORRO' },
  { name: 'Caja Municipal Ica', code: 'CMICA' },
  { name: 'Cajas Municipal Arequipa', code: 'CMAREQUIPA' },
  { name: 'Cajas Municipal Cusco', code: 'CMCUSCO' },
  { name: 'Cajas Municipal Del Santa', code: 'CMSANTA' },
  { name: 'Cajas Municipal Huancayo', code: 'CMHUANCAYO' },
  { name: 'Cajas Municipal Maynas', code: 'CMMAYNAS' },
  { name: 'Cajas Municipal Paita', code: 'CMPAITA' },
  { name: 'Cajas Municipal Piura', code: 'CMPIURA' },
  { name: 'Cajas Municipal Sullana', code: 'CMSULLANA' },
  { name: 'Cajas Municipal Tacna', code: 'CMTACNA' },
  { name: 'Cajas Municipal Trujillo', code: 'CMTRUJILLO' },
  { name: 'Cajas Rural Del Centro', code: 'CRCENTRO' },
  { name: 'Cajas Rural Incasur', code: 'CRINCASUR' },
  { name: 'Cajas Rural Los Andes', code: 'CRANDES' },
  { name: 'Cajas Rural Prymera', code: 'CRPRYMERA' },
  { name: 'Cajas Rural Raíz', code: 'CRRAIZ' },
  { name: 'Cajas Rural Sipán', code: 'CRSIPAN' },
  { name: 'Citibank', code: 'CITI' },
  { name: 'COFIDE', code: 'COFIDE' },
  { name: 'Compañía Peruana de Medios de Pago', code: 'CPMP' },
  { name: 'Cooperativa ABACO', code: 'COOPABACO' },
  { name: 'Cooperativa AELUCOOP', code: 'COOPAELU' },
  { name: 'Cooperativa de Ahorro y Credito Pacifico', code: 'COOPPACIFICO' },
  {
    name: 'Cooperativa de Ahorro y Crédito San Francisco Ltda',
    code: 'COOPSANFRAN',
  },
  { name: 'Cooperativa de Ahorro y Crédito Servicredit', code: 'COOPSERVI' },
  { name: 'Cooperativa San Francisco de Huanuco', code: 'COOPSFH' },
  { name: 'Crédito De Arequipa SA', code: 'CREDAREQUIPA' },
  { name: 'EAFC Maquisistema SA', code: 'MAQUI' },
  { name: 'EAFC Pandero SA', code: 'PANDERO' },
  { name: 'Edpyme Acceso Crediticio', code: 'EDACCESO' },
  { name: 'Edpyme Alternativa', code: 'EDALT' },
  { name: 'Edpyme BBVA Consumer Finance', code: 'EDBBVA' },
  { name: 'Edpyme Credivisión', code: 'EDCREDI' },
  { name: 'Edpyme GMG Servicios Perú', code: 'EDGMG' },
  { name: 'Edpyme Inversiones La Cruz', code: 'EDLACRUZ' },
  { name: 'Edpyme Marcimex', code: 'EDMARCI' },
  { name: 'Edpyme Mi Casita', code: 'EDMICASA' },
  { name: 'Edpyme Solidaridad', code: 'EDSOLI' },
  { name: 'Empresa de Credito Vivela SA', code: 'VIVELA' },
  { name: 'Financiera Amérika', code: 'FINAMERIKA' },
  { name: 'Financiera Compartamos', code: 'FINCOMPAR' },
  { name: 'Financiera Confianza', code: 'FINCONF' },
  { name: 'Financiera Credinka', code: 'FINCRED' },
  { name: 'Financiera Crediscotia', code: 'FINSCOTIA' },
  { name: 'Financiera Efectiva', code: 'FINEFECT' },
  { name: 'Financiera Mitsui', code: 'FINMITSUI' },
  { name: 'Financiera Oh!', code: 'FINOH' },
  { name: 'Financiera Proempresa', code: 'FINPRO' },
  { name: 'Financiera Qapaq', code: 'FINQAPAQ' },
  { name: 'Financiera TFC', code: 'FINTFC' },
  { name: 'Fondo MiVivienda', code: 'MIVIVIENDA' },
  { name: 'Fovimar', code: 'FOVIMAR' },
  { name: 'Fovime', code: 'FOVIME' },
  { name: 'Fovimfap', code: 'FOVIMFAP' },
  { name: 'Fovipol', code: 'FOVIPOL' },
  { name: 'ICBC PERU BANK', code: 'ICBC' },
  { name: 'Interbank', code: 'INTERBANK' },
  { name: 'J.P. Morgan Banco de Inversión', code: 'JPMORGAN' },
  { name: 'MiBanco', code: 'MIBANCO' },
  { name: 'Migracion', code: 'MIGRACION' },
  { name: 'Otros', code: 'OTROS' },
  { name: 'Palante Préstamos', code: 'PALANTE' },
  { name: 'Santander Consumer', code: 'SANCONS' },
  { name: 'Scotiabank Perú', code: 'SCOTIA' },
  { name: 'Sura', code: 'SURA' },
] as const;

// ============================================================================
// Bonus Program Constants
// ============================================================================

/**
 * Maximum bonus program name length
 * Default: 200 characters
 */
export const MAX_BONUS_PROGRAM_NAME_LENGTH = 200;

/**
 * Maximum bonus program description length
 * Default: 1000 characters
 */
export const MAX_BONUS_PROGRAM_DESCRIPTION_LENGTH = 1000;

/**
 * Maximum bonus program legal reference length
 * Default: 200 characters
 */
export const MAX_BONUS_LEGAL_REFERENCE_LENGTH = 200;

/**
 * Default Peruvian housing bonus programs
 * These are seeded when a new company is created
 *
 * Programs:
 * - BBP (Bono del Buen Pagador): Tiered by property value, for good payers
 * - BMV (Bono Mi Vivienda): Mi Vivienda program bonus
 * - BMS (Bono Mi Vivienda Sostenible): Sustainable housing bonus
 * - BFH (Bono Familiar Habitacional): Family housing bonus
 * - Techo Propio: Government housing program for low-income families
 * - Bono Verde: Sustainability certification bonus (combines with BBP)
 *
 * Rules based on 2024 Peruvian housing law (DS 001-2024-VIVIENDA)
 */
export const DEFAULT_BONUS_PROGRAMS = [
  {
    code: 'bbp' as const,
    name: 'Bono del Buen Pagador (BBP)',
    description:
      'Government bonus for qualified home buyers with good credit history. Amount varies by property value.',
    eligibilityRules: {
      incomeMax: 14000, // S/14,000/month max household income
      propertyMax: 427600, // S/427,600 max property value
      propertyMin: 65200, // S/65,200 min property value
      firstTimeBuyer: true,
      allowedFinancingTypes: ['mi_vivienda', 'hipotecario'] as const,
      minDownPaymentPercent: 75 / 10,
    },
    amountConfig: {
      type: 'tiered' as const,
      tiers: [
        {
          minPropertyValue: 65200,
          maxPropertyValue: 102000,
          bonusAmount: 27400,
        },
        {
          minPropertyValue: 102001,
          maxPropertyValue: 165000,
          bonusAmount: 22000,
        },
        {
          minPropertyValue: 165001,
          maxPropertyValue: 232500,
          bonusAmount: 19600,
        },
        {
          minPropertyValue: 232501,
          maxPropertyValue: 343900,
          bonusAmount: 16800,
        },
        {
          minPropertyValue: 343901,
          maxPropertyValue: 427600,
          bonusAmount: 7800,
        },
      ],
    },
    incompatibleWith: ['techo_propio', 'bfh'] as const,
  },
  {
    code: 'bmv' as const,
    name: 'Bono Mi Vivienda (BMV)',
    description:
      'Mi Vivienda program bonus for middle-income families. Requires financing through Mi Vivienda partner.',
    eligibilityRules: {
      incomeMax: 14000, // S/14,000/month max household income
      propertyMax: 427600, // S/427,600 max property value (VIS limit)
      propertyMin: 65200, // S/65,200 min property value
      firstTimeBuyer: true,
      allowedFinancingTypes: ['mi_vivienda'] as const,
      minDownPaymentPercent: 75 / 10,
    },
    amountConfig: {
      type: 'tiered' as const,
      tiers: [
        {
          minPropertyValue: 65200,
          maxPropertyValue: 102000,
          bonusAmount: 25000,
        },
        {
          minPropertyValue: 102001,
          maxPropertyValue: 165000,
          bonusAmount: 20000,
        },
        {
          minPropertyValue: 165001,
          maxPropertyValue: 232500,
          bonusAmount: 17000,
        },
        {
          minPropertyValue: 232501,
          maxPropertyValue: 343900,
          bonusAmount: 14000,
        },
        {
          minPropertyValue: 343901,
          maxPropertyValue: 427600,
          bonusAmount: 6000,
        },
      ],
    },
    incompatibleWith: ['techo_propio', 'bfh', 'bbp'] as const,
  },
  {
    code: 'bms' as const,
    name: 'Bono Mi Vivienda Sostenible (BMS)',
    description:
      'Sustainable housing bonus for properties with environmental certification. Requires 14-criteria sustainability compliance.',
    eligibilityRules: {
      incomeMax: 14000, // S/14,000/month max household income
      propertyMax: 427600, // S/427,600 max property value
      propertyMin: 65200, // S/65,200 min property value
      firstTimeBuyer: true,
      allowedFinancingTypes: ['mi_vivienda'] as const,
      minDownPaymentPercent: 75 / 10,
      requiresSustainabilityCert: true,
      sustainabilityCriteriaMin: 14,
    },
    amountConfig: {
      type: 'tiered' as const,
      tiers: [
        {
          minPropertyValue: 65200,
          maxPropertyValue: 165000,
          bonusAmount: 31000,
        },
        {
          minPropertyValue: 165001,
          maxPropertyValue: 343900,
          bonusAmount: 25000,
        },
        {
          minPropertyValue: 343901,
          maxPropertyValue: 427600,
          bonusAmount: 18000,
        },
      ],
    },
    incompatibleWith: ['techo_propio', 'bfh', 'bono_verde'] as const,
  },
  {
    code: 'bfh' as const,
    name: 'Bono Familiar Habitacional (BFH)',
    description:
      'Family housing bonus for low-income families through Techo Propio program. Higher bonus for larger families.',
    eligibilityRules: {
      incomeMax: 3715, // S/3,715/month max household income
      propertyMax: 127500, // S/127,500 max property value (Techo Propio limit)
      firstTimeBuyer: true,
      allowedFinancingTypes: [
        'mi_vivienda',
        'hipotecario',
        'direct_credit',
      ] as const,
    },
    amountConfig: {
      type: 'fixed' as const,
      fixedAmount: 47850, // S/47,850 max bonus
      maxAmount: 47850,
    },
    incompatibleWith: ['bbp', 'bmv', 'bms', 'bono_verde'] as const,
  },
  {
    code: 'techo_propio' as const,
    name: 'Techo Propio',
    description:
      'Government housing program for low-income families. Provides substantial bonus for first home purchase.',
    eligibilityRules: {
      incomeMax: 3715, // S/3,715/month max household income (purchase)
      propertyMax: 127500, // S/127,500 max property value
      firstTimeBuyer: true,
      allowedFinancingTypes: [
        'mi_vivienda',
        'hipotecario',
        'direct_credit',
      ] as const,
    },
    amountConfig: {
      type: 'fixed' as const,
      fixedAmount: 47850, // S/47,850 for purchase
      maxAmount: 47850,
    },
    incompatibleWith: ['bbp', 'bmv', 'bms', 'bono_verde'] as const,
  },
  {
    code: 'bono_verde' as const,
    name: 'Bono Verde',
    description:
      'Green building bonus for properties meeting 14 sustainability criteria. Can be combined with BBP.',
    eligibilityRules: {
      incomeMax: 14000, // S/14,000/month max household income
      propertyMax: 427600, // S/427,600 max property value
      propertyMin: 65200, // S/65,200 min property value
      firstTimeBuyer: true,
      allowedFinancingTypes: ['mi_vivienda', 'hipotecario'] as const,
      minDownPaymentPercent: 75 / 10,
      requiresSustainabilityCert: true,
      sustainabilityCriteriaMin: 14,
    },
    amountConfig: {
      type: 'fixed' as const,
      fixedAmount: 31000, // S/31,000 max bonus
      maxAmount: 31000,
    },
    incompatibleWith: ['techo_propio', 'bfh', 'bms'] as const,
  },
] as const;

/**
 * Maximum financing partner name length
 * Default: 100 characters
 */
export const MAX_FINANCING_PARTNER_NAME_LENGTH = 100;

/**
 * Maximum financing partner code length
 * Default: 20 characters
 */
export const MAX_FINANCING_PARTNER_CODE_LENGTH = 20;

/**
 * Maximum real estate code (SKU) length
 * Default: 50 characters
 */
export const MAX_REAL_ESTATE_CODE_LENGTH = 50;

/**
 * Maximum real estate S10 code length
 * Default: 50 characters
 */
export const MAX_REAL_ESTATE_S10_CODE_LENGTH = 50;

/**
 * Maximum real estate model length
 * Default: 100 characters
 */
export const MAX_MODEL_LENGTH = 100;

/**
 * Maximum registry number length
 * Default: 100 characters
 */
export const MAX_REGISTRY_NUMBER_LENGTH = 100;

/**
 * Maximum registry entry length
 * Default: 100 characters
 */
export const MAX_REGISTRY_ENTRY_LENGTH = 100;

/**
 * Maximum construction license length
 * Default: 100 characters
 */
export const MAX_CONSTRUCTION_LICENSE_LENGTH = 100;

/**
 * Maximum complaint book number length
 * Default: 100 characters
 */
export const MAX_COMPLAINT_BOOK_NUMBER_LENGTH = 100;

/**
 * Maximum legal entity name length
 * Default: 200 characters
 */
export const MAX_LEGAL_ENTITY_NAME_LENGTH = 200;

/**
 * Maximum legal entity address length
 * Default: 500 characters
 */
export const MAX_LEGAL_ENTITY_ADDRESS_LENGTH = 500;

// ============================================================================
// File Upload Constants
// ============================================================================

/**
 * Maximum photo file size in bytes (10MB)
 */
export const MAX_PHOTO_FILE_SIZE = 10 * 1024 * 1024;

/**
 * Kilobytes to bytes conversion factor
 */
export const BYTES_PER_KILOBYTE = 1024;

/**
 * Megabytes to bytes conversion factor
 */
export const BYTES_PER_MEGABYTE = BYTES_PER_KILOBYTE * BYTES_PER_KILOBYTE;

/**
 * Minimum non-final part size for R2 multipart uploads.
 */
export const R2_MULTIPART_MIN_PART_SIZE_MEGABYTES = 5;

// ============================================================================
// CSV Import Security Limits
// ============================================================================

/**
 * Maximum number of rows allowed in CSV import files
 * Prevents DoS attacks via large file uploads
 * Default: 10,000 rows
 */
export const MAX_CSV_ROW_COUNT = 10000;

/**
 * Maximum line length in CSV files (bytes)
 * Prevents memory exhaustion from malformed files
 * Default: 100KB per line
 */
export const MAX_CSV_LINE_LENGTH = 100000;

/**
 * Maximum field length in CSV files (bytes)
 * Prevents memory exhaustion from malformed fields
 * Default: 10KB per field
 */
export const MAX_CSV_FIELD_LENGTH = 10000;

// ============================================================================
// Lead Management Constants
// ============================================================================

/**
 * Maximum lead score value
 */
export const MAX_LEAD_SCORE = 100;

/**
 * Minimum lead score value
 */
export const MIN_LEAD_SCORE = 0;

/**
 * Lead score threshold for "hot" rating
 */
export const HOT_LEAD_SCORE_THRESHOLD = 70;

/**
 * Lead score threshold for "warm" rating
 */
export const WARM_LEAD_SCORE_THRESHOLD = 40;

/**
 * Maximum campaign name length
 */
export const MAX_CAMPAIGN_LENGTH = 100;

/**
 * Maximum referrer name length
 */
export const MAX_REFERRER_NAME_LENGTH = 200;

/**
 * Maximum requirements notes length
 */
export const MAX_REQUIREMENTS_NOTES_LENGTH = 2000;

/**
 * Maximum qualification notes length
 */
export const MAX_QUALIFICATION_NOTES_LENGTH = 2000;

/**
 * Maximum lost reason length
 */
export const MAX_LOST_REASON_LENGTH = 500;

/**
 * Maximum lost notes length
 */
export const MAX_LOST_NOTES_LENGTH = 2000;

/**
 * Maximum lead activity subject length
 */
export const MAX_ACTIVITY_SUBJECT_LENGTH = 200;

/**
 * Maximum lead activity description length
 */
export const MAX_ACTIVITY_DESCRIPTION_LENGTH = 5000;

/**
 * Maximum activity duration in minutes (24 hours)
 */
export const MAX_ACTIVITY_DURATION_MINUTES = 1440;

/**
 * Maximum lead task title length
 */
export const MAX_TASK_TITLE_LENGTH = 200;

/**
 * Maximum lead task description length
 */
export const MAX_TASK_DESCRIPTION_LENGTH = 2000;

/**
 * Maximum task outcome notes length
 */
export const MAX_TASK_OUTCOME_NOTES_LENGTH = 2000;

/**
 * Maximum recurrence pattern JSON length
 */
export const MAX_RECURRENCE_PATTERN_LENGTH = 500;

/**
 * Maximum property interest notes length
 */
export const MAX_INTEREST_NOTES_LENGTH = 1000;

/**
 * Maximum lead tag name length
 */
export const MAX_TAG_NAME_LENGTH = 50;

/**
 * Maximum scoring rule name length
 */
export const MAX_SCORING_RULE_NAME_LENGTH = 100;

/**
 * Maximum scoring rule points (positive)
 */
export const MAX_SCORING_RULE_POINTS = 100;

/**
 * Minimum scoring rule points (negative)
 */
export const MIN_SCORING_RULE_POINTS = -100;

/**
 * Maximum status change notes length
 */
export const MAX_STATUS_CHANGE_NOTES_LENGTH = 2000;

/**
 * Maximum assignment reason notes length
 */
export const MAX_ASSIGNMENT_NOTES_LENGTH = 2000;

/**
 * Maximum number of lead IDs accepted in one bulk delete request.
 * Queue handlers still process this payload in smaller DB batches.
 */
export const MAX_BULK_DELETE_LEADS = 500;

/**
 * Maximum number of real-estate projects that can be bulk status updated at once
 * Kept aligned with existing CRM bulk operation bounds.
 */
export const MAX_BULK_REAL_ESTATE_PROJECT_STATUS_UPDATES = 100;

/**
 * Bulk operation validation limits
 */
export const BULK_REASON_MAX_LENGTH = 200;
export const BULK_NOTES_MAX_LENGTH = 500;
export const BULK_DELETE_REASON_MIN_LENGTH = 3;
export const BULK_DELETE_REASON_MAX_LENGTH = 500;

// ============================================================================
// Opportunity Management Constants
// ============================================================================

/**
 * Maximum probability value for opportunities (percentage)
 * Used for win probability calculations
 * Default: 100%
 */
export const MAX_PROBABILITY = 100;

/**
 * Minimum probability value for opportunities (percentage)
 * Used for win probability calculations
 * Default: 0%
 */
export const MIN_PROBABILITY = 0;

/**
 * High probability threshold (success variant in UI)
 * Opportunities with >= 75% probability
 */
export const PROBABILITY_HIGH_THRESHOLD = 75;

/**
 * Medium probability threshold (info variant in UI)
 * Opportunities with >= 50% probability
 */
export const PROBABILITY_MEDIUM_THRESHOLD = 50;

/**
 * Low probability threshold (warning variant in UI)
 * Opportunities with >= 25% probability
 */
export const PROBABILITY_LOW_THRESHOLD = 25;

/**
 * Thousand divisor for currency formatting
 * Used to convert values to "k" format (e.g., $10,000 -> $10k)
 */
export const THOUSAND = 1000;

// ============================================================================
// Search Constants
// ============================================================================

/**
 * Maximum length for search query strings
 * Default: 100 characters
 */
export const MAX_SEARCH_QUERY_LENGTH = 100;

/**
 * Maximum number of search results per entity type
 * Default: 50 results
 */
export const MAX_SEARCH_LIMIT = 50;

/**
 * Default number of search results per entity type
 * Default: 20 results
 */
export const DEFAULT_SEARCH_LIMIT = 20;

// ============================================================================
// Pagination Constants
// ============================================================================

/**
 * Small pagination page size option
 * Default: 15 items per page
 */
export const PAGINATION_SIZE_SMALL = 15;

/**
 * Medium pagination page size option
 * Default: 25 items per page
 */
export const PAGINATION_SIZE_MEDIUM = 25;

/**
 * Large pagination page size option
 * Default: 50 items per page
 */
export const PAGINATION_SIZE_LARGE = 50;

/**
 * Maximum items to fetch for statistics/summary counting
 * Used when needing all items without pagination for quick stats
 * Default: 1000 items
 */
export const MAX_ITEMS_FOR_STATS = 1000;

/**
 * Maximum visible page buttons in pagination component
 * Default: 3 buttons
 */
export const MAX_VISIBLE_PAGE_BUTTONS_SMALL = 3;

/**
 * Maximum visible page buttons in pagination component (medium)
 * Default: 5 buttons
 */
export const MAX_VISIBLE_PAGE_BUTTONS_MEDIUM = 5;

/**
 * Page number offset for "..." display in pagination
 * Default: 4 pages from current
 */
export const PAGINATION_ELLIPSIS_OFFSET = 4;

// ============================================================================
// System Settings Constants
// ============================================================================

/**
 * Maximum length for email from name
 * Default: 100 characters
 */
export const MAX_FROM_NAME_LENGTH = 100;

/**
 * Maximum length for SMTP host
 * Default: 255 characters
 */
export const MAX_SMTP_HOST_LENGTH = 255;

/**
 * Maximum SMTP port number
 * Default: 65535 (max port number)
 */
export const MAX_SMTP_PORT = 65535;

/**
 * Maximum length for SMTP credentials
 * Default: 255 characters
 */
export const MAX_SMTP_CREDENTIALS_LENGTH = 255;

/**
 * Minimum password length
 * Default: 6 characters
 */
export const MIN_PASSWORD_MIN_LENGTH = 6;

/**
 * Maximum password length
 * Default: 32 characters
 */
export const MAX_PASSWORD_MIN_LENGTH = 32;

/**
 * Valid session timeout options (in minutes)
 */
export const SESSION_TIMEOUT_15_MIN = 15;
export const SESSION_TIMEOUT_30_MIN = 30;
export const SESSION_TIMEOUT_1_HOUR = 60;
export const SESSION_TIMEOUT_2_HOURS = 120;
export const SESSION_TIMEOUT_4_HOURS = 240;
export const SESSION_TIMEOUT_8_HOURS = 480;

/**
 * Minimum failed login attempts before lockout
 * Default: 3 attempts
 */
export const MIN_FAILED_LOGIN_ATTEMPTS = 3;

/**
 * Maximum failed login attempts before lockout
 * Default: 10 attempts
 */
export const MAX_FAILED_LOGIN_ATTEMPTS_MAX = 10;

/**
 * Minimum account lockout duration (in minutes)
 * Default: 5 minutes
 */
export const MIN_ACCOUNT_LOCKOUT_MINUTES = 5;

/**
 * Maximum account lockout duration (in minutes)
 * Default: 1440 minutes (24 hours)
 */
export const MAX_ACCOUNT_LOCKOUT_MINUTES = 1440;

/**
 * Maximum OpenExchangeRates App ID length
 * Default: 64 characters
 */
export const MAX_APP_ID_LENGTH = 64;

/**
 * Maximum currency exchange rate value
 * Used to prevent unreasonable rates
 * Default: 1000
 */
export const MAX_EXCHANGE_RATE_VALUE = 1000;

/**
 * Maximum error message length
 * Used for database storage
 * Default: 500 characters
 */
export const MAX_ERROR_MESSAGE_LENGTH = 500;

/**
 * Invoice due date (days after billing period start)
 * Default: 30 days
 */
export const INVOICE_DUE_DAYS = 30;

/**
 * Soft lockout threshold (days after invoice due date)
 * Default: 30 days
 */
export const SOFT_LOCKOUT_DAYS = 30;

/**
 * First reminder interval (days after due date)
 * Default: 7 days
 */
export const FIRST_REMINDER_DAYS = 7;

/**
 * Second reminder interval (days after due date)
 * Default: 14 days
 */
export const SECOND_REMINDER_DAYS = 14;

/**
 * Third reminder interval (days after due date)
 * Default: 21 days
 */
export const THIRD_REMINDER_DAYS = 21;

/**
 * Maximum licenses per request
 * Default: 100 licenses
 */
export const MAX_LICENSE_REQUEST = 100;

/**
 * Maximum monthly cost per license
 * Default: $10,000
 */
export const MAX_LICENSE_COST = 10000;

/**
 * Maximum base licenses per plan
 * Default: 1000 licenses
 */
export const MAX_BASE_LICENSES = 1000;

/**
 * Maximum billing cycle day
 * To avoid month-end issues (Feb 28/29, etc.)
 * Default: 28
 */
export const MAX_BILLING_CYCLE_DAY = 28;

/**
 * Maximum payment method string length
 * Default: 100 characters
 */
export const MAX_PAYMENT_METHOD_LENGTH = 100;

/**
 * Maximum payment reference string length
 * Default: 255 characters (database constraint)
 */
export const MAX_PAYMENT_REFERENCE_LENGTH = 255;

/**
 * Maximum notes/justification length for license operations
 * Default: 2,000 characters
 */
export const MAX_LICENSE_NOTES_LENGTH = 2000;

/**
 * Percentage multiplier for tax calculations
 * Used to convert decimal to percentage (e.g., 0.18 * 100 = 18%)
 * Default: 100
 */
export const PERCENTAGE_MULTIPLIER = 100;

/**
 * Default grace period days for late fee rules
 * Number of days after due date before late fees apply
 * Default: 5
 */
export const DEFAULT_GRACE_DAYS = 5;

/**
 * Default pagination limit for invoice and request listings
 * Default: 100 records per page
 */
export const DEFAULT_PAGINATION_LIMIT = 100;

/**
 * Minimum pagination limit (must request at least 1 item)
 * Default: 1
 */
export const MIN_PAGINATION_LIMIT = 1;

/**
 * Minimum pagination offset (start from first record)
 * Default: 0
 */
export const MIN_PAGINATION_OFFSET = 0;

/**
 * Minimum string length for required text fields
 * Default: 1 character
 */
export const MIN_STRING_LENGTH = 1;

/**
 * Maximum string length for validation
 * Default: 255 characters
 */
export const MAX_STRING_LENGTH = 255;

/**
 * Maximum invoice prefix length
 * Default: 20 characters
 */
export const MAX_INVOICE_PREFIX_LENGTH = 20;

/**
 * Maximum payment terms in days
 * Default: 365 days (1 year)
 */
export const MAX_PAYMENT_TERMS_DAYS = 365;

/**
 * Maximum overdue unsold grace days for company settings
 * Default: 3650 days (~10 years)
 */
export const MAX_OVERDUE_UNSOLD_GRACE_DAYS = 3650;

/**
 * Maximum bank account number length
 * Default: 50 characters
 */
export const MAX_BANK_ACCOUNT_LENGTH = 50;

/**
 * Minimum SWIFT/BIC code length
 * Standard SWIFT codes are 8 or 11 characters
 */
export const MIN_SWIFT_CODE_LENGTH = 8;

/**
 * Maximum SWIFT/BIC code length
 * Standard SWIFT codes are 8 or 11 characters
 */
export const MAX_SWIFT_CODE_LENGTH = 11;

/**
 * Maximum footer text length
 * Default: 1000 characters
 */
export const MAX_FOOTER_TEXT_LENGTH = 1000;

/**
 * Company ID prefix length for invoice numbers
 * First N characters of company UUID used in invoice number
 * Default: 5 characters
 */
export const COMPANY_ID_PREFIX_LENGTH = 5;

/**
 * Maximum reminder count before soft lockout
 * Reminders sent at 7, 14, 21 days (3 total)
 * Default: 3 reminders
 */
export const MAX_REMINDER_COUNT = 3;

/**
 * Third reminder level identifier
 * Used when checking if third reminder should be sent
 * Default: 3
 */
export const THIRD_REMINDER_LEVEL = 3;

/**
 * Human-readable labels for task types
 * Used consistently across the application
 */
export const TASK_TYPE_LABELS = {
  sales: 'Sales',
  legal: 'Legal',
  finance: 'Finance',
  accountant: 'Accountant',
  credit: 'Credit',
  treasury: 'Treasury',
  commercial: 'Commercial',
  'after-sales': 'After Sales',
  administration: 'Administration',
  'sales-manager': 'Sales Manager',
  customerService: 'Customer Service',
  'medical-professional': 'Medical Professional',
  'medical-assistant': 'Medical Assistant',
} as const;

// ============================================================================
// UI Constants
// ============================================================================

/**
 * Opacity for dragging elements in drag-and-drop interfaces
 */
export const DRAGGING_OPACITY = 0.5;

/**
 * Normal opacity for non-dragging elements
 */
export const NORMAL_OPACITY = 1;

// ============================================================================
// Map Constants (Leaflet)
// ============================================================================

/**
 * Leaflet marker icon width in pixels
 */
export const MARKER_ICON_WIDTH = 25;

/**
 * Leaflet marker icon height in pixels
 */
export const MARKER_ICON_HEIGHT = 41;

/**
 * Leaflet marker icon anchor X coordinate (horizontal center point)
 */
export const MARKER_ICON_ANCHOR_X = 12;

/**
 * Leaflet marker icon anchor Y coordinate (bottom point)
 */
export const MARKER_ICON_ANCHOR_Y = 41;

/**
 * Leaflet tooltip offset Y coordinate for marker labels
 */
export const MARKER_TOOLTIP_OFFSET_Y = -20;

/**
 * Leaflet map maximum zoom level
 */
export const MAP_MAX_ZOOM = 19;

/**
 * Map bounds padding (pixels)
 * Padding around markers when fitting bounds
 */
export const MAP_BOUNDS_PADDING = 50;

/**
 * Maximum zoom when fitting bounds
 * Prevents zooming too close for single markers
 */
export const MAP_FIT_BOUNDS_MAX_ZOOM = 15;

/**
 * Drag and drop activation distance (pixels)
 * Minimum movement required to activate drag operation
 */
export const DND_ACTIVATION_DISTANCE = 8;

// ============================================================================
// XLS Import/Export Constants
// ============================================================================

/**
 * Maximum XLS file size in bytes (50MB)
 * Larger than CSV/photos to support complex spreadsheets
 */
export const MAX_XLS_FILE_SIZE = 50 * 1024 * 1024;

/**
 * Maximum number of rows allowed in XLS import files
 * Reuses CSV limit for consistency
 * Default: 10,000 rows
 */
export const MAX_XLS_ROW_COUNT = MAX_CSV_ROW_COUNT;

/**
 * Magic bytes length for file type validation
 * Both XLSX (PK ZIP) and XLS (OLE) signatures are 4 bytes
 */
export const XLS_MAGIC_BYTES_LENGTH = 4;

/**
 * Number of sample rows to send for AI column mapping analysis
 */
export const XLS_SAMPLE_ROWS_COUNT = 5;

/**
 * Maximum file extension length to preserve during filename truncation
 */
export const MAX_FILE_EXTENSION_LENGTH = 10;

/**
 * Maximum reservation notes length
 * Default: 2,000 characters
 */
export const MAX_RESERVATION_NOTES_LENGTH = 2000;

/**
 * Maximum special conditions length
 * Default: 2,000 characters
 */
export const MAX_SPECIAL_CONDITIONS_LENGTH = 2000;

/**
 * Maximum transaction notes length
 * Default: 2,000 characters
 */
export const MAX_TRANSACTION_NOTES_LENGTH = 2000;

/**
 * Maximum legal notes length
 * Default: 2,000 characters
 */
export const MAX_LEGAL_NOTES_LENGTH = 2000;

/**
 * Maximum commission percentage
 * Default: 100%
 */
export const MAX_COMMISSION_PERCENT = 100;

// =============================================================================
// Proforma Field Length Constants
// =============================================================================

/**
 * Maximum proforma code length
 * Default: 100 characters
 */
export const MAX_PROFORMA_CODE_LENGTH = 100;

/**
 * Maximum proforma contract number length
 * Default: 100 characters
 */
export const MAX_PROFORMA_CONTRACT_NUMBER_LENGTH = 100;

/**
 * Maximum discount percentage
 * Default: 100%
 */
export const MAX_DISCOUNT_PERCENT = 100;

/**
 * Minimum discount percent for requests
 * Default: 0.01%
 */
export const MIN_DISCOUNT_PERCENT = 1 / 100;

/**
 * Maximum discount approval rule name length
 * Default: 100 characters
 */
export const MAX_DISCOUNT_RULE_NAME_LENGTH = 100;

/**
 * Maximum discount approval rule description length
 * Default: 500 characters
 */
export const MAX_DISCOUNT_RULE_DESCRIPTION_LENGTH = 500;

/**
 * Maximum discount justification length
 * Default: 1000 characters
 */
export const MAX_DISCOUNT_JUSTIFICATION_LENGTH = 1000;

/**
 * Maximum discount decision notes length
 * Default: 1000 characters
 */
export const MAX_DISCOUNT_NOTES_LENGTH = 1000;

/**
 * Minimum discount rule percent threshold
 * Used for minDiscountPercent, maxDiscountPercent, autoApproveBelow bounds
 * Default: 0%
 */
export const MIN_DISCOUNT_RULE_PERCENT = 0;

/**
 * Minimum discount rule priority (lower = checked first)
 * Default: 0
 */
export const MIN_DISCOUNT_RULE_PRIORITY = 0;

/**
 * Maximum payment schedule length
 * Default: 100 characters
 */
export const MAX_PAYMENT_SCHEDULE_LENGTH = 100;

/**
 * Maximum financing type length
 * Default: 100 characters
 */
export const MAX_FINANCING_TYPE_LENGTH = 100;

/**
 * Maximum direct credit indicator length
 * Default: 100 characters
 */
export const MAX_DIRECT_CREDIT_LENGTH = 100;

/**
 * Maximum payment opportunity length
 * Default: 100 characters
 */
export const MAX_PAYMENT_OPPORTUNITY_LENGTH = 100;

/**
 * Maximum wealth origin length
 * Default: 200 characters
 */
export const MAX_WEALTH_ORIGIN_LENGTH = 200;

/**
 * Maximum capture medium length
 * Default: 100 characters
 */
export const MAX_CAPTURE_MEDIUM_LENGTH = 100;

/**
 * Maximum entry channel length
 * Default: 100 characters
 */
export const MAX_ENTRY_CHANNEL_LENGTH = 100;

/**
 * Maximum bonuses description length
 * Default: 500 characters
 */
export const MAX_BONUSES_LENGTH = 500;

/**
 * Maximum UTM field length (source, medium, campaign, term, content)
 * Default: 100 characters
 */
export const MAX_UTM_FIELD_LENGTH = 100;

/**
 * Maximum proforma notes length
 * Default: 5000 characters
 */
export const MAX_PROFORMA_NOTES_LENGTH = 5000;

/**
 * Maximum subdivision code length
 * Default: 100 characters
 */
export const MAX_SUBDIVISION_CODE_LENGTH = 100;

/**
 * Maximum phone number length (for titular)
 * Default: 50 characters
 */
export const MAX_TITULAR_PHONE_LENGTH = 50;

/**
 * Maximum ID number length (for titular)
 * Default: 50 characters
 */
export const MAX_TITULAR_ID_NUMBER_LENGTH = 50;

// =============================================================================
// Excel Serial Date Conversion Constants
// =============================================================================

/**
 * Maximum valid Excel serial date number
 * Used to validate that a number is a reasonable date value
 * Default: 100000 (approximately year 2173)
 */
export const MAX_EXCEL_SERIAL_DATE = 100000;

/**
 * Excel epoch offset
 * Number of days between Unix epoch (1970-01-01) and Excel epoch (1899-12-30)
 * Excel uses 1900-01-01 as day 1, but has a bug treating 1900 as a leap year
 */
export const EXCEL_EPOCH_OFFSET = 25569;

/**
 * Seconds per day
 * Used for Excel serial date conversion
 */
export const SECONDS_PER_DAY = 86400;

/**
 * Milliseconds per second
 */
export const MS_PER_SECOND = 1000;

// ============================================================================
// Lead Engagement Scoring Constants
// ============================================================================

/**
 * Engagement point values for each event type
 * Higher points indicate stronger buying signals
 */
export const ENGAGEMENT_POINTS_EMAIL_OPEN = 1;
export const ENGAGEMENT_POINTS_LINK_CLICK = 3;
export const ENGAGEMENT_POINTS_WEBSITE_RETURN = 2;
export const ENGAGEMENT_POINTS_FORM_SUBMISSION = 5;
export const ENGAGEMENT_POINTS_DOCUMENT_DOWNLOAD = 5;
export const ENGAGEMENT_POINTS_PROPERTY_VIEW = 5;
export const ENGAGEMENT_POINTS_CALL_ANSWERED = 5;
export const ENGAGEMENT_POINTS_SOCIAL_ENGAGEMENT = 2;
export const ENGAGEMENT_POINTS_VISIT_SCHEDULED = 10;
export const ENGAGEMENT_POINTS_PROPOSAL_VIEWED = 8;
export const ENGAGEMENT_POINTS_QUOTE_REQUESTED = 10;
export const ENGAGEMENT_POINTS_MEETING_ATTENDED = 10;
export const ENGAGEMENT_POINTS_VISIT_COMPLETED = 15;
export const ENGAGEMENT_POINTS_EMAIL_REPLY = 10;
export const ENGAGEMENT_POINTS_REFERRAL_MADE = 20;
export const ENGAGEMENT_POINTS_OTHER = 1;

/**
 * Number of recent engagement events to display in summary
 * Default: 5 events
 */
export const RECENT_ENGAGEMENT_EVENTS_LIMIT = 5;

/**
 * Default limit for Next Best Action recommendations
 * Default: 10 recommendations
 */
export const DEFAULT_NBA_RECOMMENDATIONS_LIMIT = 10;

/**
 * Maximum engagement source string length
 * Default: 100 characters
 */
export const MAX_ENGAGEMENT_SOURCE_LENGTH = 100;

// ============================================================================
// Collection Constants
// ============================================================================

/**
 * Aging bucket thresholds (in days)
 */
export const AGING_BUCKET_30_DAYS = 30;
export const AGING_BUCKET_60_DAYS = 60;
export const AGING_BUCKET_90_DAYS = 90;

/**
 * Risk score thresholds
 */
export const RISK_SCORE_MAX = 100;
export const RISK_SCORE_LOW_THRESHOLD = 25;
export const RISK_SCORE_MEDIUM_THRESHOLD = 50;
export const RISK_SCORE_HIGH_THRESHOLD = 75;

/**
 * Risk calculation weights
 */
export const RISK_WEIGHT_COLLECTION_RATE = 30;
export const RISK_WEIGHT_OVERDUE_RATIO = 40;
export const RISK_WEIGHT_MAX_SEVERE_PENALTY = 30;
export const RISK_MULTIPLIER_90_PLUS_DAYS = 5;
export const RISK_MULTIPLIER_60_90_DAYS = 2;

/**
 * Collection performance thresholds
 */
export const COLLECTION_RATE_TARGET = 0.8;
export const OVERDUE_RATIO_WARNING = 0.3;
export const AVERAGE_DAYS_OVERDUE_WARNING = 45;
export const SEVERE_OVERDUE_COUNT_THRESHOLD = 5;

/**
 * Priority escalation thresholds
 */
export const PRIORITY_ESCALATION_90_DAYS = 3;
export const PRIORITY_ESCALATION_60_DAYS = 2;
export const PRIORITY_ESCALATION_30_DAYS = 1;

// ============================================================================
// Payment Servicing Agent: reminders before the due date
// ============================================================================

/** How many reminder days a company can set before a due date. */
export const COLLECTION_AGENT_MAX_BEFORE_DUE_REMINDER_DAYS = 5;

/** The earliest reminder: this many days before the due date. 0 is the due date. */
export const COLLECTION_AGENT_MAX_DAYS_BEFORE_DUE = 60;

// ============================================================================
// Payment Notification Constants
// ============================================================================

/**
 * Days before payment due date to send reminder
 */
export const PAYMENT_DUE_REMINDER_7_DAYS = 7;
export const PAYMENT_DUE_REMINDER_3_DAYS = 3;
export const PAYMENT_DUE_REMINDER_1_DAY = 1;

/**
 * Overdue severity thresholds (in days)
 * Used to determine urgency of overdue notifications
 */
export const OVERDUE_CRITICAL_DAYS = 90;
export const OVERDUE_SEVERE_DAYS = 30;

// ============================================================================
// Payment Analytics Constants
// ============================================================================

/**
 * Aging bucket day thresholds for payment analysis
 * Used for grouping overdue payments into categories
 */
export const AGING_BUCKET_DAYS_WEEK = 7;
export const AGING_BUCKET_DAYS_MONTH = 30;
export const AGING_BUCKET_DAYS_QUARTER = 90;
export const AGING_BUCKET_DAYS_YEAR = 365;

/**
 * Default batch size for collection operations
 * Used for processing overdue items, generating reports
 */
export const COLLECTION_BATCH_SIZE = 10;

/**
 * Trend comparison period range in months
 * Maximum number of months to look back for trend analysis
 */
export const TREND_COMPARISON_MONTHS = 3;

/**
 * Trend warning threshold percentage
 * Used to determine if a metric change is significant
 */
export const TREND_WARNING_THRESHOLD_PERCENT = 5;

/**
 * Search limit for collection activities
 * Used when searching through all activities to find a specific one
 */
export const COLLECTION_SEARCH_LIMIT = 1000;

/**
 * Batch status query limit
 * Used when querying batches by status for dashboard summaries
 */
export const BATCH_STATUS_LIMIT = 100;

/**
 * Debug sample size for medium-sized array slices
 * Used for displaying sample data in debug logs
 * Default: 10 items
 */
export const DEBUG_SAMPLE_SIZE_MEDIUM = 10;

/**
 * Debug sample size for larger array slices
 * Used for displaying more sample data in debug logs
 * Default: 20 items
 */
export const DEBUG_SAMPLE_SIZE_LARGE = 20;

/**
 * Short string truncation length for debug logs
 * Used for displaying abbreviated strings like emails, RUC numbers
 * Default: 20 characters
 */
export const DEBUG_TRUNCATE_SHORT = 20;

/**
 * Medium string truncation length for debug logs
 * Used for displaying abbreviated strings like phone numbers, names
 * Default: 50 characters
 */
export const DEBUG_TRUNCATE_MEDIUM = 50;

/**
 * Maximum number of warnings to include in a single log entry
 * Prevents log bloat from excessive warning arrays
 * Default: 50 warnings
 */
export const MAX_WARNINGS_IN_LOG = 50;

// ============================================================================
// Queue Monitoring Constants
// ============================================================================

/**
 * High health score threshold for queue monitoring (green)
 * Queues with health >= 95% are considered healthy
 */
export const QUEUE_HEALTH_HIGH_THRESHOLD = 95;

/**
 * Medium health score threshold for queue monitoring (yellow)
 * Queues with health >= 80% are considered acceptable
 */
export const QUEUE_HEALTH_MEDIUM_THRESHOLD = 80;

/**
 * Full percentage value
 * Used for percentage calculations
 */
export const FULL_PERCENTAGE = 100;

/**
 * Full basis-points value (100.00%)
 */
export const FULL_BASIS_POINTS = FULL_PERCENTAGE * 100;

/**
 * Maximum visible metrics per row in queue monitor
 * Default: 6 metrics
 */
export const QUEUE_MONITOR_MAX_METRICS_PER_ROW = 6;

/**
 * Maximum items to log when reporting missing/unmatched items during import validation
 * Used to avoid flooding logs with thousands of missing items
 * Default: 20 items
 */
export const IMPORT_LOG_MISSING_ITEMS_LIMIT = 20;

// ============================================================================
// POST-SALE MODULE CONSTANTS
// ============================================================================

/**
 * Maximum warranty type length
 * Default: 255 characters
 */
export const MAX_WARRANTY_TYPE_LENGTH = 255;

/**
 * Maximum prize/certificate text length
 * Default: 500 characters
 */
export const MAX_PRIZE_CERTIFICATE_LENGTH = 500;

/**
 * Maximum post-sale notes length
 * Default: 5000 characters
 */
export const MAX_POST_SALE_NOTES_LENGTH = 5000;

/**
 * Maximum change reason length
 * Default: 1000 characters
 */
export const MAX_CHANGE_REASON_LENGTH = 1000;

/**
 * Maximum requirement/ticket subject length
 * Default: 255 characters
 */
export const MAX_SUBJECT_LENGTH = 255;

/**
 * Maximum requirement/ticket description length
 * Default: 10000 characters
 */
export const MAX_DESCRIPTION_LENGTH = 10000;

/**
 * Maximum resolution/comment text length
 * Default: 10000 characters
 */
export const MAX_RESOLUTION_LENGTH = 10000;

/**
 * Ticket number padding (number of digits)
 * Example: TKT-2025-0001 (4 digits)
 */
export const TICKET_NUMBER_PADDING = 4;

/**
 * SLA due hours by priority
 * Used to calculate slaDueAt for support tickets
 */
export const SLA_HOURS_BY_PRIORITY = {
  urgent: 4, // 4 hours
  high: 24, // 24 hours
  medium: 48, // 48 hours
  low: 72, // 72 hours
} as const;

/**
 * Milliseconds per hour (for SLA calculation)
 */
export const MS_PER_HOUR = 60 * 60 * 1000;

/**
 * Company backup generation is disabled until the security, reliability,
 * retention, and restore-verification requirements are implemented.
 * Reactivation requirements are tracked in GitHub issue #1767.
 */
export const COMPANY_BACKUP_FEATURE_ENABLED = false;

// ============================================================================
// ADMIN BROADCAST MESSAGE CONSTANTS (GitHub Issue #49)
// ============================================================================

/**
 * Maximum broadcast message subject length
 * Default: 200 characters
 */
export const MAX_BROADCAST_SUBJECT_LENGTH = 200;

/**
 * Maximum broadcast message body length
 * Default: 10000 characters (HTML/Markdown content)
 */
export const MAX_BROADCAST_BODY_LENGTH = 10000;

// ============================================================================
// SYSTEM BANNER CONSTANTS (GitHub Issue #50)
// ============================================================================

/**
 * Maximum system banner title length
 * Default: 200 characters
 */
export const MAX_SYSTEM_BANNER_TITLE_LENGTH = 200;

/**
 * Maximum system banner message length
 * Default: 2000 characters
 */
export const MAX_SYSTEM_BANNER_MESSAGE_LENGTH = 2000;

/**
 * Maximum banner action label length
 * Default: 50 characters
 */
export const MAX_BANNER_ACTION_LABEL_LENGTH = 50;

/**
 * Maximum banner action URL length
 * Default: 500 characters
 */
export const MAX_BANNER_ACTION_URL_LENGTH = 500;

/**
 * Maximum banner display priority value
 * Default: 1000
 */
export const MAX_BANNER_DISPLAY_PRIORITY = 1000;

/**
 * Impersonation session timeout in minutes
 * Default: 60 minutes (1 hour)
 */
export const IMPERSONATION_SESSION_TIMEOUT_MINUTES = 60;

/**
 * Number of pagination parameters (LIMIT + OFFSET) appended to SQL query params.
 * Used when slicing params array to remove pagination params for COUNT queries.
 */
export const PAGINATION_PARAM_COUNT = 2;

/**
 * Length of an ISO date string in YYYY-MM-DD format.
 */
export const ISO_DATE_LENGTH = 10;

/**
 * Maximum length for search string query parameters.
 */
export const MAX_SEARCH_STRING_LENGTH = 200;

// The seller home lists one week of personal visits in bounded pages.
export const ASSIGNED_VISITS_PAGE_SIZE = 5;
export const ASSIGNED_VISITS_HORIZON_DAYS = 7;
export const ASSIGNED_VISITS_MAX_OFFSET = 10000;

export const PUBLIC_PROJECT_INVENTORY_MAX_PAGE_SIZE = 50;
